import { environment } from "src/environments/environment";
import * as helpers from '../../helpers/ui-helpers';
import { BuildServices, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from ".";
import { BaseJob, JobTypeLabels, StopTypeEnum } from "../job.type";
import { AnyJobBase } from "../any-job-base";
import { WaypointDetailsDialog } from "../../dialogs/waypoint-details/waypoint-details.dialog";
import { Capacitor } from "@capacitor/core";

export class OutOfServicePeriod extends AnyJobBase {

  public value: BaseJob;
  public onTheWayCompleted = !environment.features.hasOnTheWay;
  public label: string;
  public _for: string = this.translateSvc.translate('LABEL.for');
  public riderName: string;
  public type = 'out-of-service-period';

  constructor(value: BaseJob, services: BuildServices) {
    super(value, services);
    this.headerInfo.type = `${ this.translateSvc.translate('LABEL.outOfServicePeriod') } ${ this.translateSvc.translate( value.stopType === StopTypeEnum.OUT_OF_SERVICE_START ? 'ACTION.start' : 'ACTION.end') }`;
    this.label = this.translateSvc.translate('LABEL.outOfServicePeriod');
    this.actions = this.getActions();
    this.typeIcons = [ helpers.TypeIcons.outOfServicePeriod ];

    this.getActions();
    this.updateTimeBasedProperties();

    this.value.performTime = this.value.arriveTime;
  }

  public updateTimeBasedProperties = () => {
    const timeToDiff = this.scheduledStart;
    this.isLate = this.completed ? false : helpers.isInThePast(timeToDiff);
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = this.getActions();
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative({ unit: [ 'hours', 'minutes'  ], padding: 6000 });
  };

  public getActions(): RouteEventAction[] {
    const { arriveTime, flagStopId, jobType } = this.value;
    const label = this.translateSvc.translate(JobTypeLabels[jobType]);
    const actions: RouteEventAction[] =  [
      {
        type: RouteEventActionTypeMap.onTheWay,
        label: 'ACTION.onTheWay',
        show: helpers.canOnTheWayJob(this.value),
        icon: 'send',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.onTheWay(action),
      },
      {
        type: RouteEventActionTypeMap.arrive,
        label: `${ this.translateSvc.translate('ACTION.arrive') } ${ this.translateSvc.translate(this.label) }`,
        show: helpers.canArrive(this.value),
        icon: 'locate', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.arrive({ action, checkLocation: true }),
      },
      {
        type: RouteEventActionTypeMap.depart,
        label: `${ this.translateSvc.translate('ACTION.depart') } ${ this.translateSvc.translate(this.label) }`,
        show: this.value.arriveTime,
        icon: 'log-out', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.depart({ action }),
      },
      {
        type: RouteEventActionTypeMap.navigate,
        label: `ACTION.navigate`,
        show: this.value.jobEnabled && helpers.hasValidGeo(this.location),
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: true,
        disabled: false,
        fn: async () => this.locationSvc.navigateToDestination(this.location.coordinate),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios') && helpers.hasValidGeo(this.location),
        icon: 'map', color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        preview: true,
        drivingMode: false,
        disabled: false,
        fn: async () => this.locationSvc.previewLocation(this.location.coordinate, this.location.name),
      },
    ].filter(action => action.show);
    return actions;
  }

  public tapForDetails = async () => {
    const detailModal = await this.modalController.create({
      cssClass: 'parascope-full-modal',
      component: WaypointDetailsDialog,
      componentProps: { routeEvent: this },
    });
    await detailModal.present();
    const dialog = detailModal.shadowRoot.querySelector('[role="dialog"]');
    dialog.setAttribute('aria-label', 'Job Details');
  };

  public arrive = async ({ action, checkLocation }) => {
    try {
      if (action) action.disabled = true;
      const hasArrived = checkLocation ? await this.locationSvc.checkHasArrived([this]) : true;
      if (!hasArrived) { return; }
      const odoUpdated = await helpers.presentOdometer(this.modalController);
      if (!odoUpdated) { return; }
      await this.dispatchSvc.arriveJobs([ this ]);
    } catch (error) {
      console.error(error);
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };

  public depart = async ({ action }) => {
    try {
      if (action) action.disabled = true;
      await this.dispatchSvc.departJobs([ this ]);
      this.completed = true;
    } catch (error) {
      console.error(error);
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };

}
